import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Table } from "semantic-ui-react";
import { useStore } from "../../app/stores/store";
import { PagingParams } from "../../app/models/pagination";
import MyPaging from "../../app/common/form/MyPaging";
import { formatCurrency } from "../../app/common/util/functions";
import { useMemo } from "react";

type Props = {
    id: string;
}

export default observer(function PartDetailsVendors({
    id
}: Props) {
    const { t } = useTranslation(["common", "customer", "inventory"]);
    const {
        inventoryIndexStore,
        inventoryPartDetailsStore
    } = useStore();

    const { navigatedToInventoryIndexItem } = inventoryIndexStore;

    const {
        selectedPartDetails,
        getSelectedPartDetailsVendors,
        partDetailsVendorsPagingParams,
        partDetailsVendorsPagination,
        setPartDetailsVendorPagingParams,
        resetPartDetailsVendorsRegistry,
        loadInventoryPartVendors,
    } = inventoryPartDetailsStore;

    function listOnClick(value: number) {
        if (value > 0 && partDetailsVendorsPagination && value < partDetailsVendorsPagination?.totalPages + 1) {
            setPartDetailsVendorPagingParams(new PagingParams(value));
            resetPartDetailsVendorsRegistry();
            loadInventoryPartVendors(id);
        }
    }

    const vendorHasCoreTracking = useMemo(() => getSelectedPartDetailsVendors.some(v => v.coreTracking && (v.coreValue ?? 0) > 0), [getSelectedPartDetailsVendors]);

    return (
        <Table striped unstackable className="dnxTable no_padding">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        {t("labels.vendorName", { ns: "inventory" })}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        {t("labels.cost", { ns: "inventory" })}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        {t("labels.markup%", { ns: "inventory" })}
                    </Table.HeaderCell>
                    {/* <Table.HeaderCell>
                        {t("labels.coreTracking", { ns: "inventory" })}
                    </Table.HeaderCell> */}
                    {vendorHasCoreTracking && (
                        <Table.HeaderCell>
                            {t("labels.coreValue", { ns: "inventory" })}
                        </Table.HeaderCell>
                    )}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {getSelectedPartDetailsVendors && getSelectedPartDetailsVendors.length ?
                    getSelectedPartDetailsVendors.map(v => (
                        <Table.Row key={""}>
                            <Table.Cell
                                data-label={`${t("labels.vendorName", { ns: "inventory" })}:`}
                            >
                                {v.name}
                            </Table.Cell>
                            <Table.Cell data-label={`${t("labels.cost", { ns: "inventory" })}}:`}>
                                {formatCurrency(v.cost)}
                            </Table.Cell>
                            <Table.Cell
                                data-label={`${t("labels.markup%", { ns: "inventory" })}:`}
                            >
                                {v.markup}%
                            </Table.Cell>
                            {/* <Table.Cell data-label={`${t("labels.coreTracking", { ns: "inventory" })}:`}>
                                {v.coreTracking ? <Icon name="check" color="green" size="large" /> : null}
                            </Table.Cell> */}
                            {vendorHasCoreTracking && (
                                <Table.Cell data-label={`${t("labels.coreValue", { ns: "inventory" })}:`}>
                                    {formatCurrency(v.coreValue ?? 0)}
                                </Table.Cell>
                            )}
                        </Table.Row>
                    )) : (
                        <Table.Row key={"noItems"}>
                            <Table.Cell
                                data-label={`${t("labels.vendorName", { ns: "inventory" })}:`}
                            >
                            </Table.Cell>
                            <Table.Cell data-label={`${t("labels.cost", { ns: "inventory" })}:`}>
                                {formatCurrency(selectedPartDetails?.averageCost ?? 0)}
                            </Table.Cell>
                            <Table.Cell
                                data-label={`${t("labels.markup%", { ns: "inventory" })}:`}
                            >
                                {selectedPartDetails?.markup ?? navigatedToInventoryIndexItem?.markup ?? 0}%
                            </Table.Cell>
                            <Table.Cell
                                data-label={`${t("labels.quantityInStock", { ns: "inventory" })}:`}
                            >
                                {selectedPartDetails?.quantityInStock ? selectedPartDetails?.quantityInStock : selectedPartDetails?.totalQuantity ?? 0}
                            </Table.Cell>
                            <Table.Cell data-label={`${t("labels.coreValue", { ns: "inventory" })}:`}>
                                {null}
                            </Table.Cell>
                        </Table.Row>
                    )
                }
            </Table.Body>
            <Table.Footer>
                <Table.Row>
                    <Table.Cell colSpan="6">
                        <MyPaging
                            itemCount={partDetailsVendorsPagination ? partDetailsVendorsPagination.totalItems : 10}
                            pageSize={partDetailsVendorsPagingParams.pageSize}
                            currentPage={partDetailsVendorsPagination ? partDetailsVendorsPagination.currentPage : 1}
                            onClick={listOnClick}
                            onChange={listOnClick}
                            floated="right"
                        ></MyPaging>
                    </Table.Cell>
                </Table.Row>
            </Table.Footer>
        </Table>
    );
})